import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Loader2, AlertCircle, BookOpen } from 'lucide-react'
import { useResponsesStore } from '@/stores/responses-store'
import { useDraftingStore } from '@/stores/drafting-store'
import { useAgentStore } from '@/stores/agent-store'
import { useSectionAssignmentsStore, type CollaborationStatus } from '@/stores/section-assignments-store'
import { useAuthStore } from '@/stores/auth-store'
import { useBidReadOnly } from '@/hooks/useViewOnly'
import { useReviewStore } from '@/stores/review-store'
import { useUnifiedAiPanelStore } from '@/stores/unified-ai-panel-store'
import { flashSection } from '@/lib/editor-bridge'
import { OutlineNavigator } from '@/components/drafting/OutlineNavigator'
import { QuestionNavigator } from '@/components/questionnaire/QuestionNavigator'
import { DraftEditor } from '@/components/drafting/DraftEditor'
import { BriefModal } from '@/components/drafting/BriefModal'
import { BriefButton } from '@/components/drafting/BriefButton'
import { collectCitedSources, findDraftToDos, tokenDisplayText } from '@/lib/draft-stats'
import { UnifiedAiPanel } from '@/components/drafting/UnifiedAiPanel'
import type { CommentJumpContext } from '@/components/drafting/CommentCard'
import { EditorFlagBanner } from '@/components/drafting/EditorFlagBanner'
import { useCoverageStore } from '@/stores/coverage-store'
import { useSectionFlagsStore, mergeFlagsBySection } from '@/stores/section-flags-store'
import { ResizeHandle } from '@/components/ui/ResizeHandle'
import { useCommentDeepLink } from '@/hooks/useCommentDeepLink'
import { useCommentsPolling } from '@/hooks/useCommentsPolling'
import { useBidMembers } from '@/hooks/useBidMembers'
import { useResizablePanes } from '@/hooks/useResizablePanes'
import type { QuestionnaireItem } from '@/types/questionnaire-types'
import { toast } from 'sonner'
import { toastApiError } from '@/lib/api'

// Pane sizing rules live in @/lib/pane-layout (applied by useResizablePanes) —
// shared with the questionnaire and document editors and the Full response
// reader, which run the same three-pane shell.

// ===================
// Main Component
// ===================

export function SectionEditorPage() {
  const { bidId, artifactId, sectionKey } = useParams<{
    bidId: string
    artifactId: string
    sectionKey: string
  }>()
  const navigate = useNavigate()
  const { search, state: locationState } = useLocation()

  // Consume `?commentId=` deep links from comment notifications.
  useCommentDeepLink(artifactId ?? '')
  useCommentsPolling(bidId, artifactId)

  // One section per editor, so a comment on another section is a route change.
  // The Comments tab drops the key that has no outline entry.
  const commentJumpContext = useMemo<CommentJumpContext>(
    () => ({
      editorMode: 'single',
      currentSectionKey: sectionKey ?? null,
      buildSectionPath: (key) =>
        `/app/bids/${bidId ?? ''}/responses/${artifactId ?? ''}/sections/${key}`,
      // v7's navigate returns a promise; the context is fire-and-forget.
      navigate: (path) => void navigate(path),
    }),
    [bidId, artifactId, sectionKey, navigate],
  )

  // Stores
  const {
    selectedArtifact: artifact,
    isLoading: artifactLoading,
    error: artifactError,
    fetchArtifactById,
  } = useResponsesStore()

  const {
    outline,
    isLoadingOutline,
    error: outlineError,
    fetchOutline,
    updateSectionAndSave,
  } = useDraftingStore()

  const {
    isPanelOpen,
    flushDraftSavesBeforeThreadChange,
    reset: resetAgentStore,
    segments,
    initSession,
    loadReadOnlySegments,
  } = useAgentStore()

  const currentUserId = useAuthStore((s) => s.user?.id)
  // A read-only user — an org viewer, or a member who is not on the bid —
  // reads the section but never opens an AI session: the session endpoint
  // refuses them, which used to leave the editor empty.
  // Until the role and the bid are known they look like an editor; wait, or
  // the session request 403s.
  const { isReadOnly, isLoaded: isAccessLoaded } = useBidReadOnly(bidId)

  /**
   * `?issue=<key>` deep link (spec 29 §0.6.4) — the Full response reader's
   * "Fix" hands the reader here. Open the Checks tab straight away, then flash
   * the passage once the findings have actually loaded into the store.
   */
  const [searchParams] = useSearchParams()
  const issueParam = searchParams.get('issue')
  useEffect(() => {
    if (!issueParam) return
    useAgentStore.getState().openPanel()
    useUnifiedAiPanelStore.getState().setTab('review')
    const unsub = useReviewStore.subscribe((s) => {
      const f = s.activeFindings?.find((x) => x.issueKey === issueParam)
      if (f?.sectionId) {
        flashSection(f.sectionId, { excerpt: f.excerpt ?? null })
        unsub()
      }
    })
    return unsub
  }, [issueParam])

  // Coverage store: BriefButton + BriefModal derive the requirement count/chips
  // from it. The inline strip used to load it; load it here so the header
  // indicator is accurate before the modal opens.
  const loadCoverage = useCoverageStore((s) => s.load)

  // Section flags (PR 5): amber "affected by addendum" banner + navigator markers.
  const loadFlagsForArtifact = useSectionFlagsStore((s) => s.loadFlagsForArtifact)
  const artifactFlags = useSectionFlagsStore((s) => (artifactId ? s.flagsByArtifact[artifactId] : undefined))

  const {
    assignments,
    isLoading: assignmentsLoading,
    error: assignmentsError,
    currentArtifactId: assignmentsArtifactId,
    assignSection,
    unassignSection,
    updateCollaborationStatus,
    requestReview,
    submitReview,
  } = useSectionAssignmentsStore()

  // Bid members for the assignment picker, @mentions and commenter avatars.
  const bidMembers = useBidMembers(bidId)
  const isOwner = bidMembers.some((m) => m.userId === currentUserId && m.role === 'owner')

  // Section assignment handlers for sidebar
  const handleAssignSection = useCallback((sectionKey: string, userId: string) => {
    if (!artifactId) return
    assignSection(artifactId, sectionKey, userId).catch(() => {
      toast.error('Failed to assign section')
    })
  }, [artifactId, assignSection])

  const handleUnassignSection = useCallback((sectionKey: string) => {
    if (!artifactId) return
    unassignSection(artifactId, sectionKey).catch(() => {
      toast.error('Failed to unassign section')
    })
  }, [artifactId, unassignSection])

  // Compute write permission for current section
  const currentAssignment = assignments.find((a) => a.sectionId === sectionKey)
  const isFinalized = artifact?.status === 'finalized'
  const isInReview = artifact?.status === 'pending_review'
  const isFrozen = isFinalized || isInReview
  const isQuestionnaire = artifact?.artifactType === 'questionnaire'
  const canWrite = useMemo(() => {
    // Read-only users never write; finalized or under-review artifacts are always read-only
    if (isReadOnly || isFrozen) return false
    // Owner can always write
    if (isOwner) return true
    // If section is unassigned — no row, or a status-only row with a null user
    // (created just to track collaboration status) — any member can write
    if (!currentAssignment || currentAssignment.userId === null) return true
    // If assigned to current user, can write
    if (currentAssignment.userId === currentUserId) return true
    // Otherwise, locked
    return false
  }, [isReadOnly, isFrozen, isOwner, currentAssignment, currentUserId])

  // A finalised or in-review document allows plain advice and no edits (Kapil
  // decision 2026-10-08, R1 E1f): the panel keeps Ask AI there, advice-only.
  // Not for a read-only user — Ask AI needs a session they cannot open.
  const adviceOnly = isReadOnly
    ? undefined
    : isFinalized ? 'finalized' as const : isInReview ? 'pending_review' as const : undefined

  // The panel now renders for readers too (comments-only, A0), so the layout
  // math keys on isPanelOpen alone. Session bootstrapping is a different
  // question: only the Ask AI (`write`) tab calls initSession, and comments-only mode does
  // not mount it — so a read-only section still needs segments loaded here for
  // the DraftEditor to show existing draft content. Advice-only mode mounts it.
  const writeTabWillRender = isPanelOpen && (canWrite || adviceOnly !== undefined)
  useEffect(() => {
    if (!isAccessLoaded || !bidId || !artifactId || !sectionKey) return
    if (isReadOnly) {
      loadReadOnlySegments(artifactId, sectionKey).catch((err: unknown) => {
        toastApiError(err, "Couldn't load this section")
      })
      return
    }
    if (writeTabWillRender) return // UnifiedAiPanel will handle initSession
    void initSession(bidId, artifactId, sectionKey, 'unit')
  }, [isAccessLoaded, isReadOnly, writeTabWillRender, bidId, artifactId, sectionKey, initSession, loadReadOnlySegments])

  // R1 UX1: the document's state and the assignee are two different reasons;
  // passing the state as a name read "assigned to This document is under review".
  const lockedReason = isFinalized
    ? 'This document has been finalised'
    : isInReview
      ? 'This document is under review'
      : null
  const lockedByUser = !canWrite && currentAssignment
    ? (currentAssignment.fullName || currentAssignment.email)
    : null

  // Collaboration status for the current section
  const collaborationStatus: CollaborationStatus | null = currentAssignment?.collaborationStatus ?? null

  // Auto-transition: not_started → in_progress when content appears
  const autoTransitionFiredRef = useRef(false)
  useEffect(() => {
    // Reset when section changes
    autoTransitionFiredRef.current = false
  }, [sectionKey])

  useEffect(() => {
    if (autoTransitionFiredRef.current) return
    if (!artifactId || !sectionKey) return
    // Only act once assignments for this artifact have loaded — firing against
    // an empty or stale store could clobber a real status with 'in_progress'.
    if (assignmentsLoading || assignmentsError || assignmentsArtifactId !== artifactId) return
    // Skip optimistic assignments — wait for the POST to complete before auto-transitioning
    if (currentAssignment?.id.startsWith('temp-')) return
    // Unassigned sections count as not_started in document progress, so a
    // missing assignment row is promoted too (the PATCH creates a status-only entry).
    if ((currentAssignment?.collaborationStatus ?? 'not_started') !== 'not_started') return
    if (!canWrite) return

    // Check if there's any draft content for this section
    const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim()
    const hasDraftContent = (content: string) => content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().length > 0
    const matchingSegment = segments.find((seg) => seg.sectionId === sectionKey)
      ?? segments.find((seg) => {
        const section = outline?.sections.find((s) => s.sectionKey === sectionKey)
        return section && normalize(seg.heading) === normalize(section.title)
      })

    if (matchingSegment && hasDraftContent(matchingSegment.content)) {
      autoTransitionFiredRef.current = true
      void updateCollaborationStatus(artifactId, sectionKey, 'in_progress').catch(() => {
        // Non-critical — status will be updated on next explicit action
      })
    }
  }, [artifactId, sectionKey, currentAssignment, canWrite, assignmentsLoading, assignmentsError, assignmentsArtifactId, segments, outline?.sections, updateCollaborationStatus])

  // Handle collaboration status change from DraftEditor action buttons
  const handleCollaborationStatusChange = useCallback((status: CollaborationStatus) => {
    if (!artifactId || !sectionKey) return
    void updateCollaborationStatus(artifactId, sectionKey, status).catch((err) => {
      toastApiError(err, 'Failed to update section status')
    })
  }, [artifactId, sectionKey, updateCollaborationStatus])

  // Review flow handlers
  const handleRequestReview = useCallback((reviewerId: string) => {
    if (!artifactId || !sectionKey) return
    void requestReview(artifactId, sectionKey, reviewerId)
      .then(() => toast.success('Review requested'))
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to request review'))
  }, [artifactId, sectionKey, requestReview])

  const handleSubmitReview = useCallback((decision: 'approved' | 'changes_requested', comment?: string, options?: { requireReReview?: boolean }) => {
    if (!artifactId || !sectionKey) return
    void submitReview(artifactId, sectionKey, decision, comment, options)
      .then(() => toast.success(decision === 'approved' ? 'Section approved' : 'Changes requested'))
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to submit review'))
  }, [artifactId, sectionKey, submitReview])

  // Is the current user the designated reviewer for this section?
  const isReviewer = currentAssignment?.reviewerId === currentUserId && currentAssignment?.collaborationStatus === 'in_review'

  // Get the latest review round for feedback display
  const latestReviewRound = currentAssignment?.reviewRounds?.length
    ? currentAssignment.reviewRounds[currentAssignment.reviewRounds.length - 1]
    : null

  // Resizable side panes — widths shared with the other three-pane views.
  const {
    containerRef,
    outlineWidth: effectiveOutlineWidth,
    aiPanelWidth: effectiveAiPanelWidth,
    isOutlineResizing,
    isAiPanelResizing: isResizing,
    onOutlineResizeStart: handleOutlineMouseDown,
    onAiPanelResizeStart: handleMouseDown,
  } = useResizablePanes({ isAiPanelOpen: isPanelOpen })

  // Brief modal — opened from the header BriefButton.
  const [briefOpen, setBriefOpen] = useState(false)

  // Footer stats ("412 words · 3 to-dos · 8 sources"): to-dos and sources are
  // counted from this section's own segment, resolved the way DraftEditor
  // resolves the segment it saves to — the exact section key, or the session's
  // only segment (a unit session stores it as 'overview'). With several
  // segments and no exact match there is nothing safe to count.
  const footerStats = useMemo(() => {
    const exact = segments.find((seg) => seg.sectionId === sectionKey)
    const segment = exact ?? (segments.length === 1 ? segments[0] : undefined)
    const toDos = findDraftToDos(segment?.content ?? '')
    const first = toDos[0]
    return {
      toDos: {
        count: toDos.length,
        onJump: first
          ? () => {
              flashSection(sectionKey ?? '', { excerpt: tokenDisplayText(first) })
            }
          : undefined,
      },
      sources: collectCitedSources(segment ? [segment] : []),
    }
  }, [segments, sectionKey])

  // Get current section and its 1-based position in the document outline
  const currentSection = outline?.sections.find((s) => s.sectionKey === sectionKey)
  const currentSectionFlag = useMemo(() => {
    if (!sectionKey) return undefined
    return mergeFlagsBySection(artifactFlags ?? []).get(sectionKey)
  }, [artifactFlags, sectionKey])
  const documentSectionNumber = outline?.sections.findIndex((s) => s.sectionKey === sectionKey) ?? -1
  const currentSectionNumber = documentSectionNumber >= 0 ? documentSectionNumber + 1 : 1

  // Fetch data on mount
  useEffect(() => {
    if (artifactId) {
      void fetchArtifactById(artifactId)
      void fetchOutline(artifactId)
      void loadFlagsForArtifact(artifactId)
    }
    if (bidId) void loadCoverage(bidId)
  }, [artifactId, bidId, fetchArtifactById, fetchOutline, loadCoverage, loadFlagsForArtifact])

  // Reset agent state when section changes
  useEffect(() => {
    return () => {
      resetAgentStore()
    }
  }, [artifactId, sectionKey, resetAgentStore])

  // Leave this section only once its pending draft saves have landed.
  const leaveSection = (path: string, options?: { state: unknown }) => {
    void (async () => {
      if (flushDraftSavesBeforeThreadChange) {
        try {
          await flushDraftSavesBeforeThreadChange()
        } catch (error) {
          toast.error(
            error instanceof Error && error.message
              ? error.message
              : 'Please resolve draft save issues before switching sections.'
          )
          return
        }
      }
      void navigate(path, options)
    })()
  }

  // Handle section navigation
  const handleSectionSelect = (key: string) => {
    if (key === sectionKey) return
    // Keep the router state (e.g. arrived from My Work) so the breadcrumb's
    // way back survives moving between sections.
    leaveSection(`/app/bids/${bidId}/responses/${artifactId}/sections/${key}`, { state: locationState })
  }

  // The Full response reader exists for multi-section documents only; a
  // single-document response's `/document` route IS its editor. Word counts
  // aren't loaded here, so it is offered whether or not anything is written.
  const isMultiSectionDocument =
    artifact?.scopeKind === 'unit' && artifact?.artifactType === 'narrative_document'
  const handleOpenFullResponse = () => {
    leaveSection(`/app/bids/${bidId}/responses/${artifactId}/document`)
  }

  // Handle section rename
  const handleRenameSection = (sectionKey: string, title: string) => {
    if (!artifactId) return
    void updateSectionAndSave(artifactId, sectionKey, { title }).then((saved) => {
      if (!saved) toast.error("Couldn't rename the section — try again.")
    })
  }

  // Loading state
  const isLoading = artifactLoading || isLoadingOutline
  const error = artifactError || outlineError
  // Only a LOAD failure is fatal. If the artifact and outline are both in hand
  // there is a working editor on screen, and a transient write failure (rename,
  // quick-add, status fetch) must not replace it with an error page — those
  // surface as toasts at their call sites.
  const hasFatalError = Boolean(error) && (!artifact || !outline)

  if (isLoading && !artifact) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (hasFatalError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="ghost" onClick={() => { void navigate(`/app/bids/${bidId}/write`) }}>
          Back to Write
        </Button>
      </div>
    )
  }

  if (!currentSection) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Section not found</p>
        <Button variant="ghost" onClick={() => {
          // Keep the query string: a stale-section deep link degrades to the
          // response overview with its `?commentId=` intact.
          void navigate(isQuestionnaire
            ? `/app/bids/${bidId}/responses/${artifactId}/questionnaire${search}`
            : `/app/bids/${bidId}/responses/${artifactId}${search}`
          )
        }}>
          Back to Response
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden" ref={containerRef}>
      {/* Three-pane layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Pane: Outline Navigator */}
        <div
          className="bg-card shrink-0 overflow-hidden flex flex-col border-r border-border"
          style={{ width: effectiveOutlineWidth }}
          data-outline-pane="true"
        >
          {isQuestionnaire ? (
            <QuestionNavigator
              questions={(outline?.sections ?? []) as unknown as QuestionnaireItem[]}
              selectedSectionKey={sectionKey ?? null}
              onQuestionSelect={handleSectionSelect}
              assignments={assignments}
              bidMembers={bidMembers}
              isOwner={isOwner}
              isFinalized={isFrozen || isReadOnly}
              onAssignSection={handleAssignSection}
              onUnassignSection={handleUnassignSection}
              artifactId={artifactId ?? ''}
            />
          ) : (
            <OutlineNavigator
              sections={outline?.sections ?? []}
              selectedSectionKey={sectionKey ?? null}
              onSectionSelect={handleSectionSelect}
              artifactId={artifactId ?? ''}
              onRenameSection={handleRenameSection}
              bidId={bidId}
              bidMembers={bidMembers}
              isOwner={isOwner}
              isFinalized={isFrozen || isReadOnly}
              onAssignSection={handleAssignSection}
              onUnassignSection={handleUnassignSection}
            />
          )}
        </div>

        {/* Outline Resize Handle */}
        <ResizeHandle
          onMouseDown={handleOutlineMouseDown}
          isResizing={isOutlineResizing}
          aria-label="Resize outline"
        />

        {/* Center Pane: Draft Editor — the white workspace. `min-w-0` keeps it a
            well-behaved flex item; the side panes are what get clamped so this
            column keeps a readable measure. */}
        <div className="flex-1 min-w-0 overflow-hidden flex flex-col bg-card" data-editor-pane="true">
          {currentSection && (
            <BriefModal
              open={briefOpen}
              onOpenChange={setBriefOpen}
              bidId={bidId ?? ''}
              artifactId={artifactId ?? ''}
              sectionKey={sectionKey ?? ''}
              unitTitle={currentSection.title}
              scopeKind="unit"
              canWrite={canWrite}
            />
          )}
          {currentSectionFlag && (
            <EditorFlagBanner
              bidId={bidId ?? ''}
              flag={currentSectionFlag}
              readOnly={!canWrite}
            />
          )}
          <DraftEditor
            key={`${artifactId ?? ''}:${sectionKey ?? ''}`}
            bidId={bidId ?? ''}
            headerStatusSlot={
              currentSection ? (
                <div className="flex items-center gap-2">
                  <BriefButton
                    bidId={bidId ?? ''}
                    artifactId={artifactId ?? ''}
                    sectionKey={sectionKey ?? ''}
                    onOpen={() => setBriefOpen(true)}
                    compact
                  />
                  {isMultiSectionDocument && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-muted-foreground"
                      onClick={handleOpenFullResponse}
                    >
                      <BookOpen className="h-4 w-4 shrink-0" />
                      Full response view
                    </Button>
                  )}
                </div>
              ) : undefined
            }
            footerStats={footerStats}
            artifactId={artifactId ?? ''}
            sectionKey={sectionKey ?? ''}
            section={currentSection}
            documentSectionNumber={currentSectionNumber}
            canWrite={canWrite}
            lockedByUser={lockedByUser}
            lockedReason={lockedReason}
            collaborationStatus={collaborationStatus}
            onCollaborationStatusChange={handleCollaborationStatusChange}
            isReviewer={isReviewer}
            isOwner={isOwner}
            writerName={currentAssignment?.fullName || currentAssignment?.email || null}
            bidMembers={bidMembers}
            currentUserId={currentUserId}
            latestReviewRound={latestReviewRound ?? undefined}
            plannedReviewerId={currentAssignment?.plannedReviewerId ?? null}
            onRequestReview={handleRequestReview}
            onSubmitReview={handleSubmitReview}
          />
        </div>

        {/* Resize Handle */}
        {isPanelOpen && (
          <ResizeHandle
            onMouseDown={handleMouseDown}
            isResizing={isResizing}
            aria-label="Resize AI panel"
          />
        )}

        {/* Right Pane: AI Panel — comments-only when the section is read-only,
            so reviewers still have a comment surface (A0); advice-only on a
            finalised or in-review document (R1 E1f). */}
        {isPanelOpen && (
          <div
            className="border-l shrink-0 overflow-hidden flex flex-col bg-muted/25"
            style={{ width: effectiveAiPanelWidth }}
            data-ai-pane="true"
          >
            <UnifiedAiPanel
              key={`${artifactId ?? ''}:${sectionKey ?? ''}`}
              bidId={bidId ?? ''}
              artifactId={artifactId ?? ''}
              sectionKey={sectionKey ?? ''}
              scopeLabel="This section"
              bidMembers={bidMembers}
              commentJumpContext={commentJumpContext}
              commentsOnly={!canWrite && !adviceOnly}
              adviceOnly={adviceOnly}
              reviewerTabs={isReviewer}
            />
          </div>
        )}
      </div>
    </div>
  )
}
